<script setup lang="ts">
import { ref, shallowRef, computed, onMounted, onUnmounted, watch, nextTick, type Component } from "vue";
import { useRoute } from "vue-router";
import * as Sentry from "@sentry/vue";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useProject } from "@/stores/project/project.store";
import { useChatPanelStore } from "@stores/chat/chatPanel.store";
import { useFeedbackWidgetStore } from "@/stores/feedback/feedbackWidget.store";
import { useTenantFeatureAccess } from "@/composables/useTenantFeatureAccess";
import { useBreadcrumb } from "@/features/Breadcrumb";
import { storeToRefs } from "pinia";
import { useToast } from "vue-toastification";
import {
  TriangleAlert,
  Lightbulb,
  Sparkles,
  BookOpen,
  Users,
  ChevronRight,
  X,
  Image as ImageIcon,
  Video as VideoIcon,
  FileText,
  CircleCheck,
} from "@lucide/vue";
import Button from "@/components/Reusables/Buttons/Button.vue";

type Step = "intent" | "help" | "form" | "success";
type Intent = "issue" | "idea";

const COMMUNITY_HELP_URL = "https://community.realtimelca.com/c/help/";
const DOCS_URL = "https://docs.realtimelca.com";

const toast = useToast();
const route = useRoute();
const authStore = useAuthStateStore();
const projectStore = useProject();
const chatPanelStore = useChatPanelStore();
const breadcrumbStore = useBreadcrumb();
const { canUseAiChat } = useTenantFeatureAccess();

const { user } = storeToRefs(authStore);
const { project } = storeToRefs(projectStore);

const isOpen = ref(false);
const step = ref<Step>("intent");
const intent = ref<Intent>("idea");
const message = ref("");
const isSubmitting = ref(false);
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 25 * 1024 * 1024;

const MAX_ATTACHMENTS = 5;
const MAX_TOTAL_BYTES = 40 * 1024 * 1024;

type FeedbackAttachment = { id: string; data: Uint8Array; filename: string; contentType: string };

const attachments = shallowRef<FeedbackAttachment[]>([]);
const isReadingFiles = ref(false);
let attachmentsGeneration = 0;
let nextAttachmentId = 0;
const canAddAttachment = computed(() => attachments.value.length < MAX_ATTACHMENTS);
const fileInput = ref<HTMLInputElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const firstIntentCardRef = ref<HTMLButtonElement | null>(null);
const stepContentRef = ref<HTMLDivElement | null>(null);
const popoverRef = ref<HTMLDivElement | null>(null);
const containerRef = ref<HTMLDivElement | null>(null);
const popoverHeight = ref<number | null>(null);
const popoverWidth = ref<number | null>(null);
const manualHeight = ref<number | null>(null);
const isResizing = ref(false);
const transitionDirection = ref<"forward" | "back">("forward");
const consoleErrorCount = ref(0);
const stepAnnouncement = ref("");

const prefersReducedMotion = computed(() => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
});

const userName = computed(() => user.value?.name ?? "");
const userEmail = computed(() => user.value?.email ?? "");

const projectId = computed(() => project.value?.id ?? "");
const projectName = computed(() => project.value?.projectName ?? "");

const routeDisplayName = computed(() => {
  const items = breadcrumbStore.items;
  if (items.length > 0) {
    const last = items[items.length - 1];
    return last.label ?? "";
  }
  return "";
});

const draftKey = computed(() =>
  projectId.value ? `gaiup-${projectId.value}-feedback-draft` : "gaiup-no-project-feedback-draft"
);

const canSubmit = computed(() => message.value.trim().length > 0 && !isSubmitting.value);

const formPlaceholder = computed(() => {
  const place = routeDisplayName.value;
  if (intent.value === "issue") {
    return place ? `The issue I'm seeing on ${place} is…` : "The issue I'm seeing is…";
  }
  return place ? `My idea for ${place} is…` : "My idea for improving Real-Time LCA is…";
});

const TEMPLATES: Record<Intent, string[]> = {
  issue: ["What I was doing:", "What I expected:", "What happened instead:"],
  idea: ["What I'm trying to achieve:", "What's hard about it today:", "My idea:"],
};

const template = computed(() => TEMPLATES[intent.value]);
const canInsertTemplate = computed(() => !message.value.includes(template.value[0]));

const insertTemplate = () => {
  const block = template.value.join("\n\n") + "\n";
  const existing = message.value.trimEnd();
  const caretOffset = existing ? existing.length + 2 + template.value[0].length : template.value[0].length;
  message.value = existing ? `${existing}\n\n${block}` : block;
  nextTick(() => {
    textareaRef.value?.focus();
    textareaRef.value?.setSelectionRange(caretOffset, caretOffset);
  });
};

const screenshotHint = computed(() =>
  intent.value === "issue"
    ? "A screenshot or short screen recording helps us see what you see and fix it faster."
    : "A screenshot, mockup or short screen recording helps us understand your idea and decide if we can build it."
);

const formHeading = computed(() => (intent.value === "issue" ? "Tell us about the issue" : "Tell us about your idea"));

const stepTitleId = computed(() => {
  if (step.value === "intent") return "feedback-step-intent-title";
  if (step.value === "help") return "feedback-step-help-title";
  if (step.value === "form") return "feedback-step-form-title";
  return "feedback-step-success-title";
});

const activeTransitionName = computed(() => {
  if (prefersReducedMotion.value) return "fade-in";
  return transitionDirection.value === "forward" ? "fade-right" : "fade-left";
});

type HelpItem = { id: string; label: string; icon: Component; iconColor: string; onClick: () => void };

const helpItems = computed<HelpItem[]>(() => {
  const items: HelpItem[] = [];
  if (canUseAiChat.value) {
    items.push({
      id: "assistant",
      label: "Real-Time LCA AI Agent",
      icon: Sparkles,
      iconColor: "text-app-blue-600",
      onClick: openAssistant,
    });
  }
  items.push({
    id: "docs",
    label: "User docs",
    icon: BookOpen,
    iconColor: "text-type-muted",
    onClick: openDocs,
  });
  items.push({
    id: "community",
    label: "Ask the community",
    icon: Users,
    iconColor: "text-type-muted",
    onClick: openCommunityHelp,
  });
  return items;
});

const saveDraft = () => {
  try {
    localStorage.setItem(draftKey.value, JSON.stringify({ message: message.value, intent: intent.value, step: step.value }));
  } catch {
    // storage unavailable — non-critical
  }
};

const restoreDraft = () => {
  try {
    const stored = localStorage.getItem(draftKey.value);
    if (!stored) return;
    const parsed = JSON.parse(stored) as {
      message: string;
      intent: Intent;
      step: Step;
    };
    message.value = parsed.message ?? "";
    intent.value = parsed.intent ?? "idea";
    step.value = (parsed.step === "success" ? "intent" : parsed.step) ?? "intent";
  } catch {
    // corrupted data — fall back to defaults silently
  }
};

const clearDraft = () => {
  try {
    localStorage.removeItem(draftKey.value);
  } catch {
    // non-critical
  }
};

const measureAndAnimateHeight = () => {
  nextTick(() => {
    if (manualHeight.value !== null || !stepContentRef.value || !popoverRef.value) return;
    const rect = stepContentRef.value.getBoundingClientRect();
    popoverHeight.value = rect.height;
  });
};

type ResizeAxis = "x" | "y" | "both";

const MIN_POPOVER_WIDTH = 320;
const MAX_POPOVER_WIDTH = 768;
const MIN_POPOVER_HEIGHT = 200;
const RESIZE_KEY_STEP = 24;
const VIEWPORT_GUTTER = 32;
const VIEWPORT_TOP_RESERVE = 96;

const clampWidth = (width: number) => {
  const upper = Math.min(MAX_POPOVER_WIDTH, window.innerWidth - VIEWPORT_GUTTER);
  const lower = Math.min(MIN_POPOVER_WIDTH, upper);
  return Math.min(Math.max(width, lower), upper);
};

const clampHeight = (height: number) => {
  const upper = window.innerHeight - VIEWPORT_TOP_RESERVE;
  const lower = Math.min(popoverHeight.value ?? MIN_POPOVER_HEIGHT, upper);
  return Math.min(Math.max(height, lower), upper);
};

let resizeAxis: ResizeAxis = "x";
let resizeStartX = 0;
let resizeStartY = 0;
let resizeStartWidth = 0;
let resizeStartHeight = 0;

const startResize = (event: PointerEvent, axis: ResizeAxis) => {
  if (!popoverRef.value) return;
  const rect = popoverRef.value.getBoundingClientRect();
  resizeAxis = axis;
  resizeStartX = event.clientX;
  resizeStartY = event.clientY;
  resizeStartWidth = rect.width;
  resizeStartHeight = rect.height;
  isResizing.value = true;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
};

const moveResize = (event: PointerEvent) => {
  if (!isResizing.value) return;
  if (resizeAxis !== "y") popoverWidth.value = clampWidth(resizeStartWidth + (resizeStartX - event.clientX));
  if (resizeAxis !== "x") manualHeight.value = clampHeight(resizeStartHeight + (event.clientY - resizeStartY));
};

const endResize = () => {
  isResizing.value = false;
};

const resizeWithKeyboard = (event: KeyboardEvent, axis: "x" | "y") => {
  const growKey = axis === "x" ? "ArrowLeft" : "ArrowDown";
  const shrinkKey = axis === "x" ? "ArrowRight" : "ArrowUp";
  if (event.key !== growKey && event.key !== shrinkKey) return;
  event.preventDefault();
  const delta = event.key === growKey ? RESIZE_KEY_STEP : -RESIZE_KEY_STEP;
  const rect = popoverRef.value?.getBoundingClientRect();
  if (axis === "x") popoverWidth.value = clampWidth((popoverWidth.value ?? rect?.width ?? MIN_POPOVER_WIDTH) + delta);
  else manualHeight.value = clampHeight((manualHeight.value ?? rect?.height ?? MIN_POPOVER_HEIGHT) + delta);
};

let contentResizeObserver: ResizeObserver | null = null;

watch(stepContentRef, (element) => {
  contentResizeObserver?.disconnect();
  contentResizeObserver = null;
  if (!element || typeof ResizeObserver === "undefined") return;
  contentResizeObserver = new ResizeObserver(measureAndAnimateHeight);
  contentResizeObserver.observe(element);
});

const openWidget = () => {
  restoreDraft();
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
  resetForm();
  clearDraft();
};

const toggle = () => {
  if (isOpen.value) close();
  else openWidget();
};

// Another surface asked for the widget on a given flow (the settings page's "Request an integration").
const feedbackWidgetStore = useFeedbackWidgetStore();
watch(
  () => feedbackWidgetStore.openRequest,
  (request) => {
    if (!request) return;
    // After the click that asked: opening inside it registers the outside-click listener while that
    // same click is still bubbling to the document, which closed the widget in the same instant.
    setTimeout(() => {
      openWidget();
      intent.value = request.intent;
      if (request.draft) message.value = request.draft;
      navigateStep(request.intent === "issue" ? "help" : "form", "forward");
    }, 0);
  }
);

const resetForm = () => {
  step.value = "intent";
  intent.value = "idea";
  message.value = "";
  popoverHeight.value = null;
  popoverWidth.value = null;
  manualHeight.value = null;
  clearAttachments();
};

const navigateStep = (next: Step, direction: "forward" | "back") => {
  transitionDirection.value = direction;
  step.value = next;
};

const selectIntent = (next: Intent) => {
  intent.value = next;
  navigateStep(next === "issue" ? "help" : "form", "forward");
};

const goToForm = () => {
  navigateStep("form", "forward");
};

const openAssistant = () => {
  chatPanelStore.open();
  close();
};

const openCommunityHelp = () => {
  window.open(COMMUNITY_HELP_URL, "_blank", "noopener,noreferrer");
  close();
};

const openDocs = () => {
  window.open(DOCS_URL, "_blank", "noopener,noreferrer");
  close();
};

const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = Array.from(target.files ?? []);
  target.value = "";
  if (isReadingFiles.value) return;

  const generation = attachmentsGeneration;
  isReadingFiles.value = true;
  try {
    for (const file of files) {
      const isVideo = file.type.startsWith("video/");
      if (!isVideo && !file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image or video file`);
        continue;
      }
      if (!canAddAttachment.value) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files`);
        break;
      }
      if (file.size > (isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES)) {
        toast.error(`${file.name} must be under ${isVideo ? "25" : "5"} MB`);
        continue;
      }
      const totalBytes = attachments.value.reduce((sum, item) => sum + item.data.byteLength, 0);
      if (totalBytes + file.size > MAX_TOTAL_BYTES) {
        toast.error("Attachments must be under 40 MB in total");
        continue;
      }

      try {
        const data = new Uint8Array(await file.arrayBuffer());
        if (generation !== attachmentsGeneration) return;
        attachments.value = [
          ...attachments.value,
          {
            id: `attachment-${nextAttachmentId++}`,
            data,
            filename: file.name,
            contentType: file.type,
          },
        ];
      } catch (error: unknown) {
        toast.error(`Could not read ${file.name}`);
        Sentry.captureException(error);
      }
    }
  } finally {
    isReadingFiles.value = false;
  }
};

const removeAttachment = (attachmentId: string) => {
  attachments.value = attachments.value.filter((item) => item.id !== attachmentId);
};

const clearAttachments = () => {
  attachmentsGeneration++;
  attachments.value = [];
  if (fileInput.value) fileInput.value.value = "";
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isOpen.value) {
    close();
  }
};

const handleDocumentClick = (event: MouseEvent) => {
  if (!containerRef.value) return;
  // composedPath survives the clicked row being removed from the DOM before this listener runs
  if (!event.composedPath().includes(containerRef.value)) {
    close();
  }
};

const submitFeedback = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  try {
    const sentryAttachments = attachments.value.map(({ data, filename, contentType }) => ({ data, filename, contentType }));

    const tags: Record<string, string | number> = {
      feedbackType: intent.value,
      route_name: String(route.name ?? ""),
      console_errors_count: consoleErrorCount.value,
    };
    if (projectId.value) {
      tags.project_id = projectId.value;
      tags.project_name = projectName.value;
    }

    Sentry.captureFeedback(
      {
        name: userName.value,
        email: userEmail.value,
        message: `[${intent.value}] ${message.value}`,
        source: "widget",
        url: window.location.href,
        tags,
      },
      {
        includeReplay: true,
        attachments: sentryAttachments,
      }
    );

    clearDraft();
    navigateStep("success", "forward");
    setTimeout(() => {
      close();
    }, 1200);
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : "Failed to send feedback";
    toast.error(errorMessage);
    Sentry.captureException(error);
  } finally {
    isSubmitting.value = false;
  }
};

watch(isOpen, (opened) => {
  if (opened) {
    document.addEventListener("click", handleDocumentClick);
    nextTick(() => {
      firstIntentCardRef.value?.focus();
    });
  } else {
    document.removeEventListener("click", handleDocumentClick);
    nextTick(() => {
      triggerRef.value?.focus();
    });
  }
});

watch(step, (newStep) => {
  if (newStep === "form") {
    nextTick(() => {
      textareaRef.value?.focus();
    });
  }
  const labels: Record<Step, string> = {
    intent: "Step 1: What would you like to share?",
    help: "Step 2: Need help with your project?",
    form: `Step 3: ${formHeading.value}`,
    success: "Feedback submitted successfully",
  };
  stepAnnouncement.value = labels[newStep] ?? "";
});

watch(message, saveDraft);
watch(intent, saveDraft);

let originalConsoleError: typeof console.error;

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
  originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    consoleErrorCount.value++;
    originalConsoleError.apply(console, args);
  };
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.removeEventListener("click", handleDocumentClick);
  clearAttachments();
  contentResizeObserver?.disconnect();
  if (originalConsoleError) {
    console.error = originalConsoleError;
  }
});
</script>

<template>
  <div
    ref="containerRef"
    class="relative">
    <div
      role="status"
      aria-live="polite"
      class="sr-only">
      {{ stepAnnouncement }}
    </div>

    <!-- id targeted by the "Feedback" onboarding flow guide step (RTLCA-5640) -->
    <button
      id="feedback-nav-button"
      ref="triggerRef"
      type="button"
      :class="
        isOpen ? 'bg-app-blue-600 dark:bg-app-blue-fill text-white' : 'text-type-muted hover:text-type-default hover:bg-slate-100'
      "
      class="rounded-8 p-px-8 focus-ring transition-colors"
      :aria-label="isOpen ? 'Close feedback' : 'Open feedback'"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      @click="toggle">
      <span class="t-sh6">Feedback</span>
    </button>

    <Transition name="fade-top">
      <div
        v-if="isOpen"
        ref="popoverRef"
        role="dialog"
        :aria-labelledby="stepTitleId"
        class="z-dropdown rounded-8 bg-slate-0 absolute top-full right-0 mt-2 max-h-[calc(100vh-6rem)] w-[min(20rem,calc(100vw-2rem))] overflow-hidden ring-1 ring-slate-200"
        :style="{
          ...((manualHeight ?? popoverHeight) !== null && {
            height: `${manualHeight ?? popoverHeight}px`,
            transition: isResizing ? 'none' : 'height var(--duration-normal) var(--ease-standard)',
          }),
          ...(popoverWidth !== null && { width: `${popoverWidth}px` }),
        }">
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize feedback panel width"
          tabindex="0"
          :aria-valuenow="popoverWidth ?? undefined"
          :aria-valuemin="MIN_POPOVER_WIDTH"
          :aria-valuemax="MAX_POPOVER_WIDTH"
          class="focus-ring hover:bg-app-blue-200/40 z-sticky absolute top-0 left-0 h-full w-1.5 cursor-col-resize touch-none"
          :class="{ 'bg-app-blue-200/40': isResizing }"
          @pointerdown.prevent="startResize($event, 'x')"
          @pointermove="moveResize"
          @pointerup="endResize"
          @pointercancel="endResize"
          @keydown="resizeWithKeyboard($event, 'x')" />
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize feedback panel height"
          tabindex="0"
          :aria-valuenow="manualHeight ?? undefined"
          :aria-valuemin="popoverHeight ?? MIN_POPOVER_HEIGHT"
          class="focus-ring hover:bg-app-blue-200/40 z-sticky absolute bottom-0 left-0 h-1.5 w-full cursor-row-resize touch-none"
          :class="{ 'bg-app-blue-200/40': isResizing }"
          @pointerdown.prevent="startResize($event, 'y')"
          @pointermove="moveResize"
          @pointerup="endResize"
          @pointercancel="endResize"
          @keydown="resizeWithKeyboard($event, 'y')" />
        <div
          aria-hidden="true"
          class="z-sticky absolute bottom-0 left-0 size-3 cursor-nesw-resize touch-none"
          @pointerdown.prevent="startResize($event, 'both')"
          @pointermove="moveResize"
          @pointerup="endResize"
          @pointercancel="endResize" />
        <div class="h-full overflow-x-hidden overflow-y-auto">
          <div
            ref="stepContentRef"
            class="flex w-full flex-col"
            :class="{ 'min-h-full': manualHeight !== null }">
            <Transition
              :name="activeTransitionName"
              mode="out-in"
              @enter="measureAndAnimateHeight">
              <div
                v-if="step === 'intent'"
                key="intent"
                class="flex flex-col">
                <div class="border-b border-slate-200 p-4">
                  <p
                    :id="stepTitleId"
                    class="t-sh5">
                    What would you like to share?
                  </p>
                </div>
                <div class="flex gap-3 p-4">
                  <button
                    ref="firstIntentCardRef"
                    type="button"
                    class="focus-ring interactive-active text-type-default hover:bg-app-blue-200/20 duration-fast ease-standard flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-md p-4 transition-colors"
                    aria-label="Report an issue with my project"
                    @click="selectIntent('issue')">
                    <TriangleAlert
                      class="icon-lg text-app-red-500"
                      :stroke-width="1.5" />
                    <p class="t-small-bold">Issue</p>
                    <p class="t-caption text-type-muted">with my project</p>
                  </button>
                  <button
                    type="button"
                    class="focus-ring interactive-active text-type-default hover:bg-app-blue-200/20 duration-fast ease-standard flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-md p-4 transition-colors"
                    aria-label="Share an idea to improve the product"
                    @click="selectIntent('idea')">
                    <Lightbulb
                      class="icon-lg text-app-yellow-500"
                      :stroke-width="1.5" />
                    <p class="t-small-bold">Idea</p>
                    <p class="t-caption text-type-muted">to improve the product</p>
                  </button>
                </div>
              </div>

              <div
                v-else-if="step === 'help'"
                key="help"
                class="flex flex-col">
                <div class="border-b border-slate-200 p-4">
                  <p
                    :id="stepTitleId"
                    class="t-sh5">
                    Need help with your project?
                  </p>
                  <p class="t-caption text-type-muted">
                    {{
                      canUseAiChat
                        ? "Start with our AI Agent, the docs, or the community."
                        : "Browse the docs or ask the community."
                    }}
                  </p>
                </div>
                <div class="flex flex-col p-2">
                  <button
                    v-for="item in helpItems"
                    :key="item.id"
                    type="button"
                    class="focus-ring interactive-active text-type-default hover:bg-app-blue-200/20 duration-fast ease-standard flex cursor-pointer items-center gap-2 rounded-md p-2 text-left transition-colors"
                    :aria-label="item.label"
                    @click="item.onClick">
                    <component
                      :is="item.icon"
                      :class="['icon-sm', item.iconColor]"
                      :stroke-width="1.5" />
                    <p class="t-small flex-1">{{ item.label }}</p>
                    <ChevronRight
                      class="icon-xs text-type-muted"
                      :stroke-width="1.5" />
                  </button>
                </div>
                <div class="border-t border-slate-200 p-4">
                  <Button
                    label="Leave feedback instead"
                    color="button"
                    class="w-full"
                    @click="goToForm" />
                </div>
              </div>

              <div
                v-else-if="step === 'form'"
                key="form"
                class="flex flex-1 flex-col">
                <div class="border-b border-slate-200 p-4">
                  <p
                    :id="stepTitleId"
                    class="t-sh5">
                    {{ formHeading }}
                  </p>
                </div>
                <div class="flex flex-1 flex-col gap-4 p-4">
                  <button
                    v-if="canInsertTemplate"
                    type="button"
                    class="focus-ring interactive-active duration-fast ease-standard text-app-blue-700 hover:bg-app-blue-50 rounded-4 -mb-2 flex w-fit items-center gap-1.5 px-1.5 py-1 transition-colors"
                    :disabled="isSubmitting"
                    @click="insertTemplate">
                    <FileText
                      class="icon-xs"
                      :stroke-width="1.5" />
                    <span class="t-caption">Use a template</span>
                  </button>
                  <textarea
                    ref="textareaRef"
                    v-model="message"
                    rows="5"
                    :placeholder="formPlaceholder"
                    :aria-label="formHeading"
                    :readonly="isSubmitting"
                    class="focus-input rounded-8 t-small bg-slate-0 min-h-[7.5rem] w-full flex-1 resize-none border border-slate-200 p-3 leading-normal"
                    :class="{ 'opacity-50': isSubmitting }" />

                  <div class="flex flex-col gap-2">
                    <p class="t-caption text-type-muted">{{ screenshotHint }}</p>
                    <input
                      ref="fileInput"
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      class="hidden"
                      @change="handleFileSelect" />
                    <ul
                      v-if="attachments.length"
                      class="flex flex-col gap-1">
                      <li
                        v-for="item in attachments"
                        :key="item.id"
                        class="rounded-8 flex items-center gap-2 border border-slate-200 py-1 pr-1 pl-2">
                        <VideoIcon
                          v-if="item.contentType.startsWith('video/')"
                          class="icon-sm text-type-muted shrink-0"
                          :stroke-width="1.5"
                          aria-label="Video" />
                        <ImageIcon
                          v-else
                          class="icon-sm text-type-muted shrink-0"
                          :stroke-width="1.5"
                          aria-label="Image" />
                        <span class="t-caption text-type-default flex-1 truncate">{{ item.filename }}</span>
                        <button
                          type="button"
                          :aria-label="`Remove ${item.filename}`"
                          class="focus-ring interactive-active duration-fast ease-standard text-type-muted hover:text-type-default rounded-4 flex shrink-0 items-center justify-center p-1.5 transition-colors hover:bg-slate-100"
                          @click="removeAttachment(item.id)">
                          <X
                            class="icon-sm"
                            :stroke-width="1.5" />
                        </button>
                      </li>
                    </ul>
                    <button
                      v-if="canAddAttachment"
                      type="button"
                      class="rounded-8 focus-ring text-type-muted hover:text-type-default duration-fast ease-standard flex w-full cursor-pointer items-center justify-center gap-2 border border-dashed border-slate-200 p-3 transition-colors hover:border-slate-300"
                      :disabled="isReadingFiles"
                      @click="fileInput?.click()">
                      <ImageIcon
                        class="icon-sm"
                        :stroke-width="1.5" />
                      <span class="t-caption">
                        {{ attachments.length ? "Add another file" : "Add screenshots or a screen recording (recommended)" }}
                      </span>
                    </button>
                  </div>

                  <div class="flex justify-end">
                    <Button
                      label="Send"
                      color="primary"
                      :is-loading="isSubmitting"
                      :disabled="!canSubmit"
                      @click="submitFeedback" />
                  </div>
                </div>
              </div>

              <div
                v-else-if="step === 'success'"
                key="success"
                class="flex flex-col items-center gap-3 p-8">
                <CircleCheck
                  class="icon-xl text-app-green-500"
                  :stroke-width="1.5" />
                <p
                  :id="stepTitleId"
                  class="t-sh4 text-center">
                  Thanks for your feedback!
                </p>
                <p class="t-small text-type-muted text-center">We've received it.</p>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
